import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Radio, Globe2, Smartphone } from "lucide-react";

/**
 * Live visitor feed — polls /api/merchant/analytics/live every 4s.
 * Replaces SSE due to Cloudflare stream buffering in the current deployment.
 */
export default function LiveFeed({ testid = "live-feed" }) {
  const [events, setEvents] = useState([]);
  const cursorRef = useRef(null);
  const timerRef = useRef(null);

  const poll = async () => {
    try {
      const qs = cursorRef.current ? `?since=${encodeURIComponent(cursorRef.current)}` : "";
      const { data } = await api.get(`/merchant/analytics/live${qs}`);
      cursorRef.current = data.now;
      if (data.events?.length) {
        setEvents((prev) => [...data.events.reverse(), ...prev].slice(0, 30));
      }
    } catch (e) {
      if (e?.response?.status === 403) clearInterval(timerRef.current);
    }
  };

  useEffect(() => {
    poll();
    timerRef.current = setInterval(poll, 4000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pf-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <Radio className="w-3 h-3" /> Live visitor stream
        </div>
        <span className="text-[10px] text-slate-400" data-testid={`${testid}-count`}>{events.length} recent</span>
      </div>
      {events.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-6 text-center">Waiting for the next click — this feed updates automatically every 4 seconds.</div>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto">
          {events.map((e) => (
            <li key={e.id} className="bg-white/60 rounded-xl px-3 py-2 text-xs flex items-start gap-2 fade-up" data-testid={`live-event-${e.id}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-100 to-emerald-100 flex items-center justify-center flex-shrink-0">
                {e.device_type === "mobile" ? <Smartphone className="w-3.5 h-3.5" /> : <Globe2 className="w-3.5 h-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 font-medium truncate">
                  {e.country || "??"}{e.province ? ` · ${e.province}` : ""} · {e.browser || "unknown"}/{e.device_type || "?"} · via <span className="text-cyan-700">{e.traffic_source}</span>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {e.campaign_name || "campaign"} · {new Date(e.created_at).toLocaleTimeString()}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
